import { useMsal } from '@azure/msal-react';
import React, { useContext, useEffect, useState } from 'react';
import { AmplitudeEventType } from '../../../../analytics/amplitudeEvents';
import { ClientConfig } from '../../../../clientConfig';
import { AIAState } from '../../../../types/aiaState';
import { AUTO_INSTANT_ACTIVATION_PARAMS, OPENID_PARAMS } from '../../../../types/openid_enum';
import { ClientConfigContext } from '../../../clientConfigContext';
import { useBrand } from '../../../hooks/useBrand';
import { useMyChartRedirect } from '../../../hooks/useMyChartRedirect';
import { StateQueryParam, useQueryParams } from '../../../hooks/useQueryParams';
import { buildAIAEventProps, sendEvent } from '../../../services/amplitudeClient';
import { useErrorLogging } from '../../../util/errorUtils';
import getAccessToken from '../../../util/getAccessToken';
import CustomerServiceCard from '../../containers/CustomerServiceCard';
import RedirectButtonCard from '../../containers/RedirectButtonCard';
import withLogger, { ComponentLoggerProps } from '../../HOC/WithLogger';
import { AuthenticatedRoutes } from '../../routers';

function AutoInstantActivationAdditionalInfo({ log }: ComponentLoggerProps): JSX.Element {
    const { instance, accounts } = useMsal();
    const [jwt, setJwt] = useState('');
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const { brand, crossRegionEnabled } = useBrand();

    const { queryParams, error } = useQueryParams<StateQueryParam, AIAState>({
        requiredParams: ['state'],
    });

    const { getSamlSsoPage } = useMyChartRedirect();
    useErrorLogging(log);

    useEffect(() => {
        getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log).then((jwt) => {
            setJwt(jwt);
            const eventProps = buildAIAEventProps(AmplitudeEventType.SSOSignUp_AIA_NoRecordMatch_Info);
            sendEvent(eventProps, jwt);
        });
    }, []);

    const handleNext = () => {
        const eventProps = buildAIAEventProps(AmplitudeEventType.SSOSignUp_AIA_NoRecordMatch_Info_Next);
        sendEvent(eventProps, jwt);
    };

    if (error !== undefined) {
        log.error(`AutoInstantActivationAdditionalInfo(): Error occurred - ${error}`);
        return <CustomerServiceCard />;
    }

    const { ehrSystem, is_cross_region: crossRegion } = queryParams;

    const redirectURI = getSamlSsoPage(ehrSystem);

    const nextUrlParams = {
        [OPENID_PARAMS.BRAND]: brand,
        [OPENID_PARAMS.CROSS_REGION]: crossRegion ?? (crossRegionEnabled ? 'true' : 'false'),
        [OPENID_PARAMS.EHR_SYSTEM]: ehrSystem,
        [OPENID_PARAMS.REDIRECT_URI]: redirectURI,
        [AUTO_INSTANT_ACTIVATION_PARAMS.REGISTER_PHONE]: 'true',
    };

    return (
        <RedirectButtonCard
            title='Provide additional information'
            content='We couldn’t find a match. Please provide additional information to help us locate your medical record.'
            url={AuthenticatedRoutes.RootRoutes.Registration}
            onNext={handleNext}
            params={nextUrlParams}
        />
    );
}

export default withLogger(AutoInstantActivationAdditionalInfo, { eventSource: 'AutoInstantActivationError' });
